/**
 * The scenes of the gameplay page: each a few seconds of the real game, scripted (see stage.ts). Units on the left
 * belong to player 0 (red), on the right to player 1 (blue).
 */
import { paintRect, setCell, Physique, STATE_GAS, STATE_LIQUID, type World } from '@invaders/sim';
import {
  WEAPON_BAZOOKA, WEAPON_GRENADE, WEAPON_PISTOL, WEAPON_SANDBAG, WEAPON_DRILL, WEAPON_MINE, WEAPON_WAVE, WEAPON_FLAMER,
  WEAPON_COCKTAIL, WEAPON_CRYO, WEAPON_ICE_MINE, WEAPON_METEOR, WEAPON_LASER, WEAPON_GEYSER, WEAPON_DOLL, WEAPON_REMOTE,
  WEAPON_JETPACK, WEAPON_BATTERY, WEAPON_ACID_BARREL, WEAPON_POWDER_BARREL, WEAPON_VOLCANO, WEAPON_ROOMBA, burstBarrel, landRoomba, spawnBarrel, FP, PROJ_LAVA_BOMB, WEAPONS, MAX_LAUNCH_SPEED, MAX_THROW_SPEED,
  PROJECTILE_GRAVITY, THROW_GRAVITY, PFP, PFP_SHIFT, FP_SHIFT, UNIT_W, UNIT_H, FROZEN_PENDING, MINE_ICE,
  ZORG, GOLEM, SANDMAN, FISHMAN, TREEMAN, ROBOTMAN, BARBE_NOIRE, LIZARDMAN, SNOWMAN,
  damageUnit, freezeArea, killUnit, placeMine, triggerLegacies,
  type GameState, type Unit,
} from '@invaders/game';
import { FLOOR, STAGE_H, STAGE_W, plainGround, type Scene, type SceneCtx } from './stage';

const { EMPTY, ROCK, SAND, WATER, LAVA, WOOD, OIL, ICE, FIRE } = Physique;

/** Middle of a unit's body, in cells. */
function middle(u: Unit): [number, number] {
  return [(u.x >> FP_SHIFT) + (UNIT_W >> 1), (u.y >> FP_SHIFT) + (UNIT_H >> 1)];
}

/**
 * The power (0-100) that lands a shot of `weapon` fired along `angle` from `from` on `to`, on flat ground (display
 * only: the scenes are aimed by hand, the game never does this).
 */
function powerFor(weapon: number, from: Unit, to: Unit, angle: number): number {
  const lob = WEAPONS[weapon].lob === true;
  const g = (lob ? THROW_GRAVITY : PROJECTILE_GRAVITY) / PFP;
  const vmax = (lob ? MAX_THROW_SPEED : MAX_LAUNCH_SPEED) / PFP;
  const dx = Math.abs(middle(to)[0] - middle(from)[0]);
  const v = Math.sqrt((dx * g) / Math.sin((2 * angle * Math.PI) / 180));
  return Math.max(1, Math.min(100, Math.round((v / vmax) * 100)));
}

/** The demos do not wait for the geyser's 5 s: it springs in `ticks`. */
function soon(c: SceneCtx, ticks: number): void {
  c.direct(() => { for (const g of c.state.geysers) g.wait = ticks; });
}

/** A shot of `weapon` from unit `u` (angle in degrees, 0 = right, 90 = up). */
function fire(c: SceneCtx, u: Unit, weapon: number, angle: number, power = 0, extra: { tx?: number; ty?: number; link?: number; sx?: number; sy?: number; rot?: number } = {}): void {
  c.send({ type: 'fire', player: u.player, unit: u.id, weapon, angle, power, ...extra });
}

/** Aimed at unit `to` (a mirrored angle when it is on the left). */
function shootAt(c: SceneCtx, from: Unit, to: Unit, weapon: number, angle: number, scale = 1): void {
  const left = middle(to)[0] < middle(from)[0];
  fire(c, from, weapon, left ? 180 - angle : angle, Math.round(powerFor(weapon, from, to, angle) * scale));
}

const walk = (c: SceneCtx, u: Unit, dir: -1 | 0 | 1) => c.send({ type: 'walk', player: u.player, unit: u.id, dir });
const jump = (c: SceneCtx, u: Unit) => c.send({ type: 'jump', player: u.player, unit: u.id });

/** A pool dug into the plain ground between x0 and x1, `depth` cells deep, full of `mat`. */
function pool(world: World, x0: number, x1: number, depth: number, mat: number): void {
  paintRect(world, x0, FLOOR, x1, FLOOR + depth - 1, mat);
}

/** A tree of wood: a trunk and a crown of branches. */
function tree(world: World, x: number, floor = FLOOR): void {
  paintRect(world, x - 1, floor - 26, x + 1, floor - 1, WOOD);
  for (let k = 0; k < 4; k++) paintRect(world, x - 12 + k * 2, floor - 26 - k * 3, x + 12 - k * 2, floor - 25 - k * 3, WOOD);
}

/** The rising lava of the end of the world: up to row `top`, filling the empty cells, the gases and the liquids. */
function riseLava(state: GameState, top: number): void {
  const { w, h } = state.world.physique;
  const st = state.world.rules.materials.state;
  for (let y = h - 1; y >= top; y--) {
    for (let x = 0; x < w; x++) {
      const m = Physique.get(state.world.physique, x, y);
      if (m !== LAVA && (st[m] === STATE_GAS || st[m] === STATE_LIQUID)) setCell(state.world, x, y, LAVA);
    }
  }
}

/** A lava bomb from the sky (the end of the world), falling on column x. */
function lavaBomb(state: GameState, x: number, vx: number): void {
  state.projectiles.push({ x: (x << PFP_SHIFT), y: 0, vx: Math.round(vx * PFP), vy: PFP, weapon: PROJ_LAVA_BOMB, owner: -1, age: 0, link: -1 });
}

// --- the goal of the game -------------------------------------------------------------------------------------------

export const GOAL_SCENES: Record<'matter' | 'last' | 'death' | 'sky' | 'end', Scene> = {
  // Every pixel is matter: sand falls, water runs, lava meets water.
  matter: {
    ticks: 330,
    terrain(world) {
      plainGround(world);
      paintRect(world, 150, FLOOR - 20, 156, FLOOR - 1, ROCK);
      paintRect(world, 236, FLOOR - 26, 242, FLOOR - 1, ROCK);
    },
    setup() {},
    at(t, c) {
      if (t === 10) c.send({ type: 'paint', x: 40, y: 20, radius: 12, mat: SAND });
      if (t === 60) c.send({ type: 'paint', x: 110, y: 30, radius: 12, mat: WATER });
      if (t === 120) c.send({ type: 'paint', x: 200, y: 30, radius: 10, mat: WATER });
      if (t === 170) c.send({ type: 'paint', x: 205, y: 22, radius: 7, mat: LAVA });
    },
  },
  // The last team standing: a shell, and the enemy's last invader falls.
  last: {
    ticks: 240,
    setup(c) {
      c.unit(0, ZORG, 30);
      c.unit(0, FISHMAN, 56);
      c.unit(1, SANDMAN, 196).hp = 40;
    },
    at(t, c) {
      if (t === 40) shootAt(c, c.state.units[0], c.state.units[2], WEAPON_BAZOOKA, 40);
    },
  },
  // Death changes the map: a Treeman falls, his stele grows a tree over three turns.
  death: {
    ticks: 330,
    setup(c) { c.unit(1, TREEMAN, 120); },
    at(t, c) {
      if (t === 40) { c.direct(() => killUnit(c.state, c.state.units[0])); c.caption('Death burst: a wooden stump'); }
      for (let k = 1; k <= 3; k++) if (t === 60 + k * 60) { c.direct(() => triggerLegacies(c.state)); c.caption(`Legacy · turn ${k} of 3`); }
    },
  },
  // The sky sends the weapons: a chest under its parachute, opened by walking into it.
  sky: {
    ticks: 340,
    setup(c) {
      c.unit(0, ZORG, 80);
      c.send({ type: 'chest', x: 150, content: WEAPON_LASER });
    },
    at(t, c) {
      if (t === 100) walk(c, c.state.units[0], 1);
      if (t === 260) walk(c, c.state.units[0], 0);
    },
  },
  // The end of the world: the lava rises from the bottom, lava bombs fall from the sky.
  end: {
    ticks: 360,
    terrain(world) {
      plainGround(world);
      for (let x = 0; x < STAGE_W; x++) {
        const top = FLOOR + 8 + Math.round(6 * Math.sin(x / 18));
        paintRect(world, x, top, x, STAGE_H - 1, EMPTY);
      }
      paintRect(world, 0, FLOOR, STAGE_W - 1, FLOOR + 4, ROCK);
      paintRect(world, 0, STAGE_H - 4, STAGE_W - 1, STAGE_H - 1, ROCK);
    },
    setup(c) {
      c.unit(0, ROBOTMAN, 50);
      c.unit(1, GOLEM, 180);
    },
    at(t, c) {
      if (t % 45 === 10 && t < 280) {
        const k = Math.floor(t / 45);
        lavaBomb(c.state, 20 + ((k * 97) % 220), ((k % 3) - 1) * 0.3);
      }
      if (t === 60) riseLava(c.state, STAGE_H - 10);
      if (t === 180) riseLava(c.state, FLOOR + 6);
    },
  },
};

// --- a turn ----------------------------------------------------------------------------------------------------------

export const TURN_SCENES: Scene[] = [
  // 1. Start of the turn: a chest falls, the legacies fire.
  {
    ticks: 300,
    setup(c) {
      c.unit(0, ZORG, 40);
      const s = c.unit(1, SANDMAN, 190);
      c.direct(() => killUnit(c.state, s));
    },
    at(t, c) {
      if (t === 30) { c.send({ type: 'chest', x: 110, content: WEAPON_CRYO }); c.caption('A chest falls from the sky, drifting with the wind'); }
      if (t === 150) { c.direct(() => triggerLegacies(c.state)); c.caption('The legacies of the dead fire (here: sand)'); }
    },
  },
  // 2. Aim (30 s): choose, ACTION, walk, jump, open a chest, one shot.
  {
    ticks: 440,
    setup(c) {
      c.unit(0, ZORG, 30);
      c.unit(1, GOLEM, 200);
      c.send({ type: 'chest', x: 74, content: WEAPON_GRENADE });
    },
    at(t, c) {
      const me = c.state.units[0];
      if (t === 10) c.caption('Pick a character, then ACTION');
      if (t === 120) { walk(c, me, 1); c.caption('Walk, jump, open a chest…'); }
      if (t === 150) jump(c, me);
      if (t === 175) walk(c, me, 0);
      if (t === 180) { shootAt(c, me, c.state.units[1], WEAPON_GRENADE, 50, 0.9); c.caption('…then one shot'); }
    },
  },
  // 3. Retreat (3 s): move away, no more shooting.
  {
    ticks: 320,
    setup(c) {
      c.unit(0, LIZARDMAN, 110);
      c.unit(1, FISHMAN, 190);
    },
    at(t, c) {
      const me = c.state.units[0];
      if (t === 20) { shootAt(c, me, c.state.units[1], WEAPON_GRENADE, 55, 0.8); c.caption('The shot is gone…'); }
      if (t === 40) { walk(c, me, -1); c.caption('3 seconds to run for cover'); }
      if (t === 120) jump(c, me);
      if (t === 170) walk(c, me, 0);
    },
  },
  // 4. Resolution: everything settles, the contact damage, the next player.
  {
    ticks: 300,
    terrain(world) { plainGround(world); pool(world, 150, 230, 6, LAVA); },
    setup(c) {
      c.unit(0, ZORG, 30);
      const t = c.unit(1, TREEMAN, 100);
      t.burning = true;
    },
    at(t, c) {
      if (t === 10) c.caption('Wait until nothing moves any more…');
      if (t === 30) c.send({ type: 'paint', x: 108, y: FLOOR - 4, radius: 4, mat: FIRE });
      if (t === 120) c.caption('…then fire, lava and acid hurt whoever touches them');
      if (t === 150) c.direct(() => damageUnit(c.state, c.state.units[1], 10, 'fire'));
      if (t === 220) c.caption('Next player!');
    },
  },
];

// --- the weapons -------------------------------------------------------------------------------------------------------

/** A shooter on the left (Zorg) and a target on the right, both on the plain ground. */
function duel(kind = SANDMAN, x = 186): (c: SceneCtx) => void {
  return (c) => { c.unit(0, ZORG, 28); c.unit(1, kind, x); };
}

export const WEAPON_SCENES: Record<number, Scene> = {
  [WEAPON_BAZOOKA]: {
    ticks: 210,
    setup: duel(GOLEM),
    at(t, c) { if (t === 30) shootAt(c, c.state.units[0], c.state.units[1], WEAPON_BAZOOKA, 35); },
  },
  [WEAPON_GRENADE]: {
    ticks: 300,
    setup: duel(GOLEM, 176),
    at(t, c) { if (t === 30) shootAt(c, c.state.units[0], c.state.units[1], WEAPON_GRENADE, 50, 0.85); },
  },
  [WEAPON_PISTOL]: {
    ticks: 180,
    setup: duel(GOLEM),
    at(t, c) { if (t === 30 || t === 55 || t === 80) fire(c, c.state.units[0], WEAPON_PISTOL, 0); },
  },
  [WEAPON_SANDBAG]: {
    ticks: 240,
    setup: duel(GOLEM, 176),
    at(t, c) { if (t === 30) shootAt(c, c.state.units[0], c.state.units[1], WEAPON_SANDBAG, 50); },
  },
  [WEAPON_DRILL]: {
    ticks: 260,
    terrain(world) { plainGround(world, 40); },
    setup(c) { c.unit(0, ROBOTMAN, 30); },
    at(t, c) {
      const me = c.state.units[0];
      if (t === 30) c.send({ type: 'drill', player: 0, unit: me.id, angle: -55 });
      if (t === 80) c.send({ type: 'aim', player: 0, unit: me.id, angle: 0 });
      if (t === 145) c.send({ type: 'aim', player: 0, unit: me.id, angle: 35 });
    },
  },
  [WEAPON_MINE]: {
    ticks: 330,
    setup: duel(SANDMAN, 200),
    at(t, c) {
      const [me, foe] = c.state.units;
      if (t === 20) fire(c, me, WEAPON_MINE, 45, powerFor(WEAPON_MINE, me, foe, 45) - 14);
      if (t === 200) walk(c, foe, -1);
    },
  },
  [WEAPON_WAVE]: {
    ticks: 240,
    terrain(world) { plainGround(world); paintRect(world, 120, FLOOR - 6, 140, FLOOR - 1, SAND); },
    setup: duel(GOLEM, 170),
    at(t, c) { if (t === 30) fire(c, c.state.units[0], WEAPON_WAVE, 0); },
  },
  [WEAPON_FLAMER]: {
    ticks: 300,
    terrain(world) { plainGround(world); tree(world, 150); },
    setup: duel(TREEMAN, 110),
    at(t, c) {
      const me = c.state.units[0];
      if (t === 30) fire(c, me, WEAPON_FLAMER, 8);
      if (t === 60) c.send({ type: 'aim', player: 0, unit: me.id, angle: 0 });
      if (t === 90) c.send({ type: 'aim', player: 0, unit: me.id, angle: 12 });
    },
  },
  [WEAPON_COCKTAIL]: {
    ticks: 300,
    terrain(world) { plainGround(world); tree(world, 210); },
    setup: duel(TREEMAN, 170),
    at(t, c) { if (t === 30) shootAt(c, c.state.units[0], c.state.units[1], WEAPON_COCKTAIL, 50); },
  },
  [WEAPON_CRYO]: {
    ticks: 330,
    terrain(world) { plainGround(world); pool(world, 120, 230, 10, WATER); },
    setup(c) { c.unit(0, ZORG, 28); c.unit(1, FISHMAN, 160); },
    at(t, c) { if (t === 30) shootAt(c, c.state.units[0], c.state.units[1], WEAPON_CRYO, 50, 0.9); },
  },
  [WEAPON_ICE_MINE]: {
    ticks: 330,
    terrain(world) { plainGround(world); pool(world, 60, 110, 8, WATER); },
    setup(c) {
      c.unit(0, ZORG, 20);
      c.unit(1, SANDMAN, 160);
      placeMine(c.state, 128, FLOOR - 3, 0, MINE_ICE);
    },
    at(t, c) { if (t === 125) walk(c, c.state.units[1], -1); },
  },
  [WEAPON_METEOR]: {
    ticks: 240,
    setup: duel(GOLEM, 170),
    at(t, c) { if (t === 30) fire(c, c.state.units[0], WEAPON_METEOR, 0, 0, { tx: 178, ty: FLOOR - 8 }); },
  },
  [WEAPON_LASER]: {
    ticks: 180,
    terrain(world) {
      plainGround(world);
      paintRect(world, 0, 0, 6, FLOOR, ROCK);
      paintRect(world, STAGE_W - 7, 0, STAGE_W - 1, FLOOR, ROCK);
      paintRect(world, 0, 0, STAGE_W - 1, 5, ROCK);
      paintRect(world, 150, FLOOR - 40, 162, FLOOR - 1, ICE);
    },
    setup(c) { c.unit(0, ZORG, 40, 10); c.unit(1, GOLEM, 200, 10); },
    at(t, c) { if (t === 30) fire(c, c.state.units[0], WEAPON_LASER, 32); },
  },
  [WEAPON_GEYSER]: {
    ticks: 260,
    setup: duel(GOLEM, 170),
    at(t, c) { if (t === 30) { fire(c, c.state.units[0], WEAPON_GEYSER, 0, 0, { tx: 178, ty: FLOOR - 2 }); c.caption('It waits: 5 s into the next turn, it springs'); } if (t === 31) soon(c, 90); },
  },
  [WEAPON_DOLL]: {
    ticks: 270,
    setup: duel(GOLEM, 190),
    at(t, c) { if (t === 30) fire(c, c.state.units[0], WEAPON_DOLL, 80, 48, { link: 1 }); },
  },
  [WEAPON_REMOTE]: {
    ticks: 270,
    w: 384,
    h: 168,
    setup(c) { c.unit(0, ZORG, 30); c.unit(1, GOLEM, 250); },
    at(t, c) {
      const floor = Math.round((FLOOR * 168) / STAGE_H);
      if (t === 40) fire(c, c.state.units[0], WEAPON_REMOTE, 0, 0, { sx: 258, sy: floor + 20, tx: 120, ty: 52, rot: 1 });
    },
  },
  [WEAPON_JETPACK]: {
    ticks: 280,
    terrain(world) { plainGround(world); paintRect(world, 120, FLOOR - 30, 150, FLOOR - 1, ROCK); },
    setup(c) { c.unit(0, ZORG, 40); },
    at(t, c) {
      const me = c.state.units[0];
      if (t === 20) c.send({ type: 'jetpack', player: 0, unit: me.id });
      if (t === 30) c.send({ type: 'thrust', player: 0, unit: me.id, on: true });
      if (t === 55) walk(c, me, 1);
      if (t === 75) c.send({ type: 'thrust', player: 0, unit: me.id, on: false });
      if (t === 95) c.send({ type: 'thrust', player: 0, unit: me.id, on: true });
      if (t === 115) c.send({ type: 'thrust', player: 0, unit: me.id, on: false });
      if (t === 190) walk(c, me, 0);
    },
  },
  [WEAPON_BATTERY]: {
    ticks: 420,
    terrain(world) { plainGround(world); pool(world, 140, 236, 6, WATER); },
    setup(c) { c.unit(0, ZORG, 28); c.unit(1, FISHMAN, 160); c.unit(1, GOLEM, 210); },
    at(t, c) { if (t === 20) fire(c, c.state.units[0], WEAPON_BATTERY, 45, powerFor(WEAPON_BATTERY, c.state.units[0], c.state.units[1], 45) - 8); },
  },
  [WEAPON_ACID_BARREL]: {
    ticks: 420,
    terrain: plainGround,
    setup(c) { c.unit(0, ZORG, 24); c.unit(1, GOLEM, 226); },
    at(t, c) {
      if (t === 20) fire(c, c.state.units[0], WEAPON_ACID_BARREL, 50, 22);
      if (t === 240 && c.state.barrels.length > 0) { c.direct(() => burstBarrel(c.state, 0)); c.caption('Hurt, it sprays its acid'); }
    },
  },
  [WEAPON_POWDER_BARREL]: {
    ticks: 420,
    terrain: plainGround,
    setup(c) { c.unit(0, ZORG, 24); c.unit(1, GOLEM, 226); },
    at(t, c) {
      if (t === 20) fire(c, c.state.units[0], WEAPON_POWDER_BARREL, 50, 22);
      if (t === 240 && c.state.barrels.length > 0) { c.direct(() => burstBarrel(c.state, 0)); c.caption('Hurt, it blows up, and its trail goes with it'); }
    },
  },
  [WEAPON_ROOMBA]: {
    ticks: 720,
    terrain(world) { plainGround(world); pool(world, 120, 216, 6, WATER); paintRect(world, 70, FLOOR - 7, 92, FLOOR - 1, SAND); },
    setup(c) { c.unit(0, ZORG, 12); },
    at(t, c) {
      if (t === 20) { fire(c, c.state.units[0], WEAPON_ROOMBA, 60, 16); c.caption('It walks, it looks ahead…'); }
      if (t === 150) c.caption('It sees what is not solid: a whirlwind opens, and it swallows it all');
    },
  },
  [WEAPON_VOLCANO]: {
    ticks: 600,
    terrain: plainGround,
    setup(c) { c.unit(0, ZORG, 28); c.unit(1, GOLEM, 210); },
    at(t, c) { if (t === 30) fire(c, c.state.units[0], WEAPON_VOLCANO, 0, 0, { tx: 128, ty: FLOOR - 8 }); },
  },
};

// --- the invaders: their death burst, then the three turns of their legacy ------------------------------------------------

/** Some legacies need something around to act on: water for the Snowman and the Robotman. */
const NEEDS_WATER = new Set([SNOWMAN, ROBOTMAN]);

export function invaderScene(kind: number): Scene {
  const wet = NEEDS_WATER.has(kind);
  return {
    ticks: 340,
    terrain(world) { plainGround(world); if (wet) pool(world, 124, 220, 8, WATER); },
    setup(c) {
      c.unit(1, kind, wet ? 100 : 120);
      if (kind === BARBE_NOIRE) c.unit(0, GOLEM, 60);
    },
    at(t, c) {
      if (t === 10) c.caption('');
      if (t === 50) {
        c.direct(() => killUnit(c.state, c.state.units[0]));
        c.caption(kind === ZORG ? 'Death: his saucer takes him away (no legacy)' : 'Death burst');
      }
      if (kind === ZORG) return;
      for (let k = 1; k <= 3; k++) if (t === 80 + k * 70) { c.direct(() => triggerLegacies(c.state)); c.caption(`Legacy · turn ${k} of 3`); }
    },
  };
}

// --- the synergies ---------------------------------------------------------------------------------------------------------

/** A ground of rock, without the sand of the plain scenes: a Roomba would eat it. */
function rockGround(world: World): void {
  paintRect(world, 0, FLOOR, STAGE_W - 1, STAGE_H - 1, ROCK);
}

export const SYNERGY_SCENES: Record<string, Scene> = {
  'wave-icemine': {
    ticks: 380,
    setup(c) { c.unit(0, SANDMAN, 20); placeMine(c.state, 140, FLOOR - 3, 0, MINE_ICE); },
    at(t, c) { if (t === 125) fire(c, c.state.units[0], WEAPON_WAVE, 0); },
  },
  'wave-electric': {
    ticks: 300,
    setup(c) {
      c.unit(0, SANDMAN, 20);
      const robot = c.unit(1, ROBOTMAN, 170);
      c.unit(1, GOLEM, 110);
      c.direct(() => killUnit(c.state, robot));
    },
    at(t, c) { if (t === 40) fire(c, c.state.units[0], WEAPON_WAVE, 0); },
  },
  'wave-cocktail': {
    ticks: 280,
    terrain(world) { plainGround(world); paintRect(world, 70, FLOOR - 4, 84, FLOOR - 1, OIL); },
    setup(c) { c.unit(0, SANDMAN, 20); c.unit(1, TREEMAN, 190); },
    at(t, c) {
      if (t === 20) c.send({ type: 'paint', x: 77, y: FLOOR - 6, radius: 2, mat: FIRE });
      if (t === 40) fire(c, c.state.units[0], WEAPON_WAVE, 0);
    },
  },
  'geyser-cryo': {
    ticks: 330,
    setup(c) { c.unit(0, SANDMAN, 20); },
    at(t, c) {
      if (t === 20) fire(c, c.state.units[0], WEAPON_GEYSER, 0, 0, { tx: 150, ty: FLOOR - 2 });
      if (t === 21) soon(c, 0);
      if (t === 70) c.direct(() => freezeArea(c.state, 150, FLOOR - 24, 36, 14));
    },
  },
  'pistol-mine': {
    ticks: 260,
    setup(c) { c.unit(0, ZORG, 20); c.unit(1, SANDMAN, 150); placeMine(c.state, 140, FLOOR - 3, 0); },
    at(t, c) {
      if (t === 130) {
        const [mx, my] = middle(c.state.units[0]);
        const angle = -Math.round((Math.atan2(FLOOR - 3 - my, 140 - mx) * 180) / Math.PI);
        fire(c, c.state.units[0], WEAPON_PISTOL, angle);
      }
    },
  },
  'laser-ice': {
    ticks: 180,
    terrain(world) { plainGround(world); paintRect(world, 190, FLOOR - 34, 204, FLOOR - 1, ICE); },
    setup(c) { c.unit(0, ZORG, 120); c.unit(1, GOLEM, 50); },
    at(t, c) { if (t === 30) fire(c, c.state.units[0], WEAPON_LASER, 0); },
  },
  'remote-lava': {
    ticks: 280,
    w: 384,
    h: 168,
    terrain(world) {
      plainGround(world);
      const floor = Math.round((FLOOR * 168) / STAGE_H);
      paintRect(world, 10, floor, 130, floor + 14, LAVA);
    },
    setup(c) { c.unit(0, ZORG, 150); c.unit(1, GOLEM, 300); },
    at(t, c) {
      const floor = Math.round((FLOOR * 168) / STAGE_H);
      // The square holds only lava (and air): it falls on the enemy once it is put down above him.
      if (t === 40) fire(c, c.state.units[0], WEAPON_REMOTE, 0, 0, { sx: 70, sy: floor - 35, tx: 308, ty: floor - 68, rot: 0 });
    },
  },
  'doll-geyser': {
    ticks: 360,
    w: 384,
    h: 168,
    setup(c) { c.unit(0, ZORG, 30); c.unit(1, GOLEM, 300); },
    at(t, c) {
      const floor = Math.round((FLOOR * 168) / STAGE_H);
      if (t === 20) fire(c, c.state.units[0], WEAPON_GEYSER, 0, 0, { tx: 100, ty: floor - 2 });
      if (t === 21) soon(c, 0);
      if (t === 22) fire(c, c.state.units[0], WEAPON_DOLL, 30, 45, { link: 1 });
    },
  },
  'bazooka-frozen': {
    ticks: 220,
    setup(c) {
      c.unit(0, ZORG, 28);
      const e = c.unit(1, SANDMAN, 180);
      e.frozen = FROZEN_PENDING;
    },
    at(t, c) { if (t === 50) shootAt(c, c.state.units[0], c.state.units[1], WEAPON_BAZOOKA, 35); },
  },
  'acid-powder': {
    ticks: 300,
    setup(c) {
      c.unit(0, ZORG, 20); c.unit(1, GOLEM, 196);
      spawnBarrel(c.state, WEAPON_POWDER_BARREL, 112, FLOOR - 1, 0, 0);
      spawnBarrel(c.state, WEAPON_ACID_BARREL, 134, FLOOR - 1, 0, 0);
    },
    at(t, c) { if (t === 40) fire(c, c.state.units[0], WEAPON_PISTOL, 0); },
  },
  'powder-flamer': {
    ticks: 420,
    setup(c) { c.unit(0, ZORG, 20); c.unit(1, GOLEM, 226); spawnBarrel(c.state, WEAPON_POWDER_BARREL, 64, FLOOR - 1, 2 * FP, 0); },
    at(t, c) { if (t === 230) { fire(c, c.state.units[0], WEAPON_FLAMER, 0); c.caption('The flame runs along the fuse, up to the barrel'); } },
  },
  'pistol-acid': {
    ticks: 300,
    setup(c) { c.unit(0, ZORG, 20); c.unit(1, GOLEM, 152); spawnBarrel(c.state, WEAPON_ACID_BARREL, 136, FLOOR - 1, 0, 0); },
    at(t, c) { if (t === 40) fire(c, c.state.units[0], WEAPON_PISTOL, 0); },
  },
  'volcano-powder': {
    ticks: 480,
    setup(c) { c.unit(0, ZORG, 20); c.unit(1, GOLEM, 186); spawnBarrel(c.state, WEAPON_POWDER_BARREL, 156, FLOOR - 1, 0, 0); },
    at(t, c) { if (t === 20) fire(c, c.state.units[0], WEAPON_VOLCANO, 0, 0, { tx: 128, ty: FLOOR - 8 }); },
  },
  'volcano-wave': {
    ticks: 420,
    setup(c) { c.unit(0, ZORG, 20); },
    at(t, c) {
      if (t === 10) fire(c, c.state.units[0], WEAPON_VOLCANO, 0, 0, { tx: 150, ty: FLOOR - 8 });
      if (t === 240) { fire(c, c.state.units[0], WEAPON_WAVE, 0); c.caption('The wave sets the lava into obsidian'); }
    },
  },
  'roomba-wave': {
    ticks: 300,
    terrain: rockGround,
    setup(c) { c.unit(0, ZORG, 20); landRoomba(c.state, 170, FLOOR - 1, -1, 0); },
    at(t, c) { if (t === 40) fire(c, c.state.units[0], WEAPON_WAVE, 0); },
  },
  'roomba-volcano': {
    ticks: 560,
    terrain: rockGround,
    setup(c) { c.unit(0, ZORG, 20); landRoomba(c.state, 186, FLOOR - 1, -1, 0); },
    at(t, c) { if (t === 20) fire(c, c.state.units[0], WEAPON_VOLCANO, 0, 0, { tx: 128, ty: FLOOR - 8 }); },
  },
  'roomba-powder': {
    ticks: 720,
    terrain: rockGround,
    setup(c) { c.unit(0, ZORG, 12); spawnBarrel(c.state, WEAPON_POWDER_BARREL, 96, FLOOR - 1, FP, 0); landRoomba(c.state, 62, FLOOR - 1, 1, 0); },
    at(t, c) { if (t === 200) c.caption('The fuse is gone before anyone can light it'); },
  },
};
